import { useEffect, useRef, useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import useSWR from 'swr';
import { get, post, del } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PrizeProductDialog } from '@/components/domain/PrizeProductDialog';
import { tierLabel, bySortOrder, type PrizeProduct } from '@/lib/prize-product';

type Payload = { products: PrizeProduct[] };
type DialogState = { open: boolean; mode: 'new' | 'edit' | 'duplicate'; seed: PrizeProduct | null };
type RowActions = { onEdit: () => void; onDuplicate: () => void; onRemove: () => void };

export default function PrizeProducts() {
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState<DialogState>({ open: false, mode: 'new', seed: null });
  const { data, isLoading, mutate } = useSWR<Payload>('/api/prize-products', get);

  const [ordered, setOrdered] = useState<PrizeProduct[]>([]);
  const orderedRef = useRef<PrizeProduct[]>([]);
  useEffect(() => {
    if (data?.products) {
      const next = [...data.products].sort(bySortOrder);
      orderedRef.current = next;
      setOrdered(next);
    }
  }, [data]);

  const setOrder = (next: PrizeProduct[]) => { orderedRef.current = next; setOrdered(next); };

  async function persistOrder() {
    try {
      await post('/api/prize-products/reorder', { ids: orderedRef.current.map((p) => p.id) });
      // A reorder writes sortOrder to every product, so every row in this list
      // now has a NEW version (`_rev`) and a new sortOrder. Re-read before the
      // operator opens the editor: without this their save would carry the
      // pre-drag version and the server would (correctly) refuse it as stale.
      void mutate();
    } catch { /* order stays optimistic; next load re-syncs from the server */ }
  }

  async function remove(p: PrizeProduct) {
    if (!confirm(`Delete prize product "${p.displayName}" (${p.id})? This cannot be undone.`)) return;
    await del(`/api/prize-products/${encodeURIComponent(p.id)}`);
    void mutate();
  }

  const dialogFor = (p: PrizeProduct): RowActions => ({
    onEdit: () => setDialog({ open: true, mode: 'edit', seed: p }),
    onDuplicate: () => setDialog({ open: true, mode: 'duplicate', seed: p }),
    onRemove: () => void remove(p),
  });

  const canReorder = !q.trim();
  const n = q.toLowerCase();
  const filtered = canReorder ? ordered : ordered.filter((p) =>
    [p.id, p.displayName, p.garment, p.color].some((v) => String(v || '').toLowerCase().includes(n)));

  return (
    <div className="px-7 py-6">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Prize Products</h1>
          <p className="text-[12px] text-muted">Fundraiser prize catalog · {ordered.length} total · drag to set the New Submission dropdown order</p>
        </div>
        <Button onClick={() => setDialog({ open: true, mode: 'new', seed: null })}>New product</Button>
      </div>

      <div className="mb-3 max-w-[360px]">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, id, garment, color…" />
      </div>

      <div className="rounded-md bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3"><Skeleton className="h-7 w-full" /><Skeleton className="h-7 w-full" /><Skeleton className="h-7 w-full" /></div>
        ) : !filtered.length ? (
          <div className="p-8 text-center text-[12px] text-muted">{canReorder ? 'No prize products yet.' : `No prize products match "${q}".`}</div>
        ) : canReorder ? (
          <Reorder.Group axis="y" values={ordered} onReorder={setOrder} className="list-none">
            {ordered.map((p) => (
              <DragRow key={p.id} p={p} actions={dialogFor(p)} onDragEnd={() => void persistOrder()} />
            ))}
          </Reorder.Group>
        ) : (
          filtered.map((p) => <StaticRow key={p.id} p={p} actions={dialogFor(p)} />)
        )}
      </div>

      <PrizeProductDialog
        open={dialog.open}
        mode={dialog.mode}
        seed={dialog.seed}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        onSaved={() => void mutate()}
      />
    </div>
  );
}

function RowBody({ p, actions, grip }: { p: PrizeProduct; actions: RowActions; grip: React.ReactNode }) {
  return (
    <>
      {grip}
      {p.mockup?.baseImage ? (
        <img
          src={`/api/prize-products/mockups/${encodeURIComponent(p.mockup.baseImage)}`}
          alt=""
          loading="lazy"
          className="h-9 w-9 shrink-0 rounded-[6px] bg-bg object-contain"
        />
      ) : (
        <span className="h-9 w-9 shrink-0 rounded-[6px] bg-bg" aria-hidden />
      )}
      <span className="w-44 shrink-0 truncate font-mono text-[10px] text-subtle">{p.id}</span>
      <span className="flex-1 truncate text-[12px]">{p.displayName || 'Unnamed'}</span>
      <Badge className="bg-bg text-muted">{tierLabel(p)}</Badge>
      {p.active === false && <Badge className="bg-red-100 text-red-700">inactive</Badge>}
      <button className="text-[11px] text-muted hover:text-text" onClick={actions.onEdit}>Edit</button>
      <button className="text-[11px] text-muted hover:text-text" onClick={actions.onDuplicate}>Duplicate</button>
      <button className="text-[11px] text-red-600 hover:text-red-700" onClick={actions.onRemove}>Delete</button>
    </>
  );
}

function DragRow({ p, actions, onDragEnd }: { p: PrizeProduct; actions: RowActions; onDragEnd: () => void }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={p}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      className="flex items-center gap-3 border-b border-divider bg-surface px-3 py-2 last:border-b-0"
    >
      <RowBody p={p} actions={actions} grip={
        <span onPointerDown={(e) => controls.start(e)} className="cursor-grab select-none px-1 text-subtle hover:text-text" title="Drag to reorder">⠿</span>
      } />
    </Reorder.Item>
  );
}

function StaticRow({ p, actions }: { p: PrizeProduct; actions: RowActions }) {
  return (
    <div className="flex items-center gap-3 border-b border-divider px-3 py-2 last:border-b-0">
      <RowBody p={p} actions={actions} grip={<span className="select-none px-1 text-divider">⠿</span>} />
    </div>
  );
}
